import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { apiWithContext } from '../../services/api';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  Card,
  Autocomplete,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Collapse,
  Divider,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Check as ApproveIcon,
  Cancel as RejectIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  FileDownload as ExportIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HelpOutline as HelpOutlineIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { format } from 'date-fns';
import { arDZ } from 'date-fns/locale';
import {
  WarehouseTransferRequest,
  WarehouseTransferRequestFilters,
  PaginatedWarehouseTransferRequestResponse
} from '../../types/warehouseTransferRequest';

// Types
interface Warehouse {
  _id: string;
  name: string;
  location?: string;
}

// Interface for transfer event status
interface TransferEventStatus {
  status: 'success' | 'failed' | 'in_progress' | 'no_events' | 'unknown';
  hasErrors: boolean;
  totalEvents?: number;
  failedEvents?: number;
  successEvents?: number;
}

// مكون الدائرة لحالة الأحداث
interface EventStatusIndicatorProps {
  status: TransferEventStatus | undefined;
  loading: boolean;
  referenceNumber: string;
  productCount?: number;
}

const EventStatusIndicator: React.FC<EventStatusIndicatorProps> = ({ status, loading, referenceNumber, productCount }) => {
  if (loading) {
    return (
      <Tooltip title="جاري التحقق من حالة الأحداث...">
        <CircularProgress size={20} />
      </Tooltip>
    );
  }

  if (!status || status.status === 'no_events' || status.status === 'unknown') {
    return (
      <Tooltip title="لا توجد أحداث مسجلة لهذا الطلب">
        <HelpOutlineIcon sx={{ color: 'grey.400', fontSize: 24 }} />
      </Tooltip>
    );
  }

  // حساب العدد المتوقع من الأحداث = (عدد المنتجات × 4) + 2
  const expectedEventCount = productCount ? (productCount * 4) + 2 : 0;
  const actualEventCount = status.totalEvents || 0;
  const eventsMatch = expectedEventCount > 0 && actualEventCount === expectedEventCount;

  if (status.status === 'success') {
    if (eventsMatch) {
      // الأحداث متطابقة - لون أخضر
      return (
        <Tooltip title={`جميع الأحداث نجحت (${status.successEvents || 0} حدث) - متطابقة مع العدد المتوقع`}>
          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
        </Tooltip>
      );
    } else {
      // الأحداث غير متطابقة - لون أصفر
      return (
        <Tooltip title={`عدد الأحداث (${actualEventCount}) غير متطابقة مع العدد المتوقع (${expectedEventCount})`}>
          <ErrorIcon sx={{ color: 'warning.main', fontSize: 24 }} />
        </Tooltip>
      );
    }
  }

  if (status.status === 'failed' || status.hasErrors) {
    return (
      <Tooltip
        title={`يوجد أخطاء في الأحداث (${status.failedEvents || 0} فاشل من ${status.totalEvents || 0})`}
      >
        <ErrorIcon sx={{ color: 'error.main', fontSize: 24 }} />
      </Tooltip>
    );
  }

  if (status.status === 'in_progress') {
    return (
      <Tooltip title="الطلب قيد التنفيذ">
        <CircularProgress size={20} sx={{ color: 'warning.main' }} />
      </Tooltip>
    );
  }

  return null;
};

// Component for the Warehouse Transfer Request List
const WarehouseTransferRequestList: React.FC = () => {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  // معلومات المستخدم الحالي (يمكن استبدالها بمكتبة إدارة الحالة مثل Redux أو Context)
  const [currentUser] = useState({
    role: 'admin', // افتراضياً أن المستخدم هو مدير النظام
    warehouses: [] as string[] // قائمة المستودعات التي يديرها المستخدم
  });
  
  // State for requests data
  const [requests, setRequests] = useState<WarehouseTransferRequest[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalRequests, setTotalRequests] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  
  // State for warehouses (for filtering)
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  
  // State for filtering
  const [filters, setFilters] = useState<WarehouseTransferRequestFilters>({
    status: undefined,
    fromWarehouse: undefined,
    toWarehouse: undefined,
    startDate: undefined,
    endDate: undefined,
  });
  const [showFilters, setShowFilters] = useState<boolean>(false);
  
  // حالات الأحداث للطلبات (للدوائر الخضراء/الحمراء)
  const [transferStatuses, setTransferStatuses] = useState<Record<string, TransferEventStatus>>({});
  const [loadingStatuses, setLoadingStatuses] = useState<boolean>(false);
  
  // State for alerts and dialogs
  const [alert, setAlert] = useState<{type: 'success' | 'error', message: string} | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [requestToDelete, setRequestToDelete] = useState<string | null>(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState<boolean>(false);
  const [requestToApprove, setRequestToApprove] = useState<string | null>(null);
  const [changeStatusDialogOpen, setChangeStatusDialogOpen] = useState<boolean>(false);
  const [requestToChangeStatus, setRequestToChangeStatus] = useState<string | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState<boolean>(false);
  const [requestToView, setRequestToView] = useState<WarehouseTransferRequest | null>(null);
  
  // State for refresh
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  // Fetch warehouses on component mount
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const { apiWithContext } = await import('../../services/api');
        const response = await apiWithContext.get('/warehouse-transfer-requests/warehouses', 'warehouse-transfer-request').then(res => res.data);
        if (response.success) {
          setWarehouses(response.warehouses);
        }
      } catch (error) {
        console.error('Error fetching warehouses:', error);
        // Fallback to general warehouses endpoint
        try {
          const response = await axios.get(`${API_BASE_URL}/warehouses`);
          if (response.data.success) {
            setWarehouses(response.data.warehouses);
          }
        } catch (fallbackError) {
          console.error('Error fetching warehouses from fallback:', fallbackError);
        }
      }
    };
    
    fetchWarehouses();
  }, []);
  
  // جلب حالات الأحداث للطلبات المعروضة
  useEffect(() => {
    const fetchTransferStatuses = async () => {
      if (requests.length === 0) return;
      
      setLoadingStatuses(true);
      try {
        // جمع أرقام المستندات من الطلبات (نستخدم _id كرقم مرجعي)
        const referenceNumbers = requests
          .map(r => r._id)
          .filter(Boolean);
        
        if (referenceNumbers.length === 0) {
          setLoadingStatuses(false);
          return;
        }

        // طلب حالات جميع الطلبات دفعة واحدة
        const response = await axios.post(
          `${API_BASE_URL}/inventory-events/transfer-status/bulk`,
          { referenceNumbers },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        if (response.data.success) {
          setTransferStatuses(response.data.statuses);
        }
      } catch (error) {
        console.error('خطأ في جلب حالات الطلبات:', error);
        // لا نعرض خطأ للمستخدم لأن هذه ميزة إضافية
      } finally {
        setLoadingStatuses(false);
      }
    };

    // تأخير بسيط لتجنب الطلبات المتزامنة
    const timer = setTimeout(() => {
      fetchTransferStatuses();
    }, 500);

    return () => clearTimeout(timer);
  }, [requests]);
  
  // Fetch requests based on current page, rows per page, and filters
  useEffect(() => {
    const fetchRequests = async () => {
      setIsLoading(true);
      
      // Prepare query parameters outside try/catch so they're accessible in both
      const params = new URLSearchParams();
      params.append('page', (page + 1).toString());
      params.append('limit', rowsPerPage.toString());
      
      // Add filters if they exist
      if (filters.status) params.append('status', filters.status);
      if (filters.fromWarehouse) params.append('fromWarehouse', filters.fromWarehouse);
      if (filters.toWarehouse) params.append('toWarehouse', filters.toWarehouse);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      
      try {
        // Try both possible API endpoint patterns
        console.log(`Attempting to fetch warehouse transfer requests...`);
        
        let endpoint = `${API_BASE_URL}/warehouse-transfer-requests`;
        let response;
        
        console.log(`API_BASE_URL:`, API_BASE_URL);
        try {
          console.log(`Trying primary endpoint: ${endpoint}`);
          response = await apiWithContext.get(`${endpoint}?${params.toString()}`, 'warehouse-transfer-requests') as {
            data: {
              success: boolean;
              message: string;
              data: PaginatedWarehouseTransferRequestResponse;
            }
          };
          console.log(`Primary endpoint succeeded: ${endpoint}`);
        } catch (primaryError: any) {
          if (primaryError.response?.status === 404) {
            // Try alternative endpoint pattern
            endpoint = `${API_BASE_URL}/warehouse-transfer-requests`;
            console.log(`Primary endpoint failed with 404. Trying alternative endpoint: ${endpoint}`);
            response = await apiWithContext.get(`${endpoint}?${params.toString()}`, 'warehouse-transfer-requests') as {
              data: {
                success: boolean;
                message: string;
                data: PaginatedWarehouseTransferRequestResponse;
              }
            };
            console.log(`Alternative endpoint succeeded: ${endpoint}`);
          } else {
            // If not a 404 error, rethrow
            throw primaryError;
          }
        }
        
        // Log response for debugging
        console.log(`Response received from ${endpoint}:`, response.status, response.statusText);
        
        if (response.data.success) {
          setRequests(response.data.data.requests);
          setTotalRequests(response.data.data.pagination.total);
        } else {
          throw new Error(response.data.message);
        }
      } catch (error: any) {
        console.error('Error fetching warehouse transfer requests:', error);
        console.error('Request details:', {
          url: `${API_BASE_URL}/warehouse-transfer-requests`,
          params: Object.fromEntries(params.entries()),
          errorStatus: error.response?.status,
          errorData: error.response?.data
        });
        
        // Clear previous API error alerts on new attempts
        setAlert(null);
        
        // If we got a 404, it might be an API endpoint issue
        if (error.response?.status === 404) {
          console.log('Received 404 - Trying alternate endpoint path...');
          try {
            // Try an alternative endpoint path format
            const altResponse = await axios.get(`${API_BASE_URL}/warehouse-transfer-requests?${params.toString()}`);
            if (altResponse.data) {
              console.log('Alternative endpoint worked!');
              // Handle different response formats
              if (altResponse.data.success && altResponse.data.data) {
                setRequests(altResponse.data.data.requests || []);
                setTotalRequests(altResponse.data.data.pagination?.total || 0);
              } else if (Array.isArray(altResponse.data)) {
                setRequests(altResponse.data);
                setTotalRequests(altResponse.data.length);
              }
              return; // Exit early if successful
            }
          } catch (altError: any) {
            console.error('Alternative endpoint also failed:', altError);
          }
        }
        
        setAlert({
          type: 'error',
          message: error.response?.status === 404
            ? 'خطأ 404: لا يمكن الوصول إلى قائمة طلبات التحويل بين المستودعات. يرجى التحقق من إعدادات النظام أو الاتصال بالدعم الفني.'
            : error.response?.data?.message || error.message || 'حدث خطأ أثناء جلب طلبات التحويل'
        });
        setRequests([]);
        setTotalRequests(0);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchRequests();
  }, [page, rowsPerPage, filters, refreshTrigger]);
  
  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handle refresh
  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };
  
  // Reset filters
  const resetFilters = () => {
    setFilters({
      status: undefined,
      fromWarehouse: undefined,
      toWarehouse: undefined,
      startDate: undefined,
      endDate: undefined,
    });
  };
  
  // Update filters
  const updateFilter = (key: keyof WarehouseTransferRequestFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPage(0); // Reset to first page when filter changes
  };
  
  // Navigate to create new request page
  const handleCreateRequest = () => {
    navigate('/warehouse-transfers/requests/new');
  };
  
  // Navigate to edit request page
  const handleEditRequest = (requestId: string) => {
    navigate(`/warehouse-transfers/requests/edit/${requestId}`);
  };
  
  // Open delete confirmation dialog
  const confirmDelete = (requestId: string) => {
    setRequestToDelete(requestId);
    setDeleteDialogOpen(true);
  };
  
  // Delete a request
  const handleDeleteRequest = async () => {
    if (!requestToDelete) return;
    
    try {
      const response = await axios.delete(`${API_BASE_URL}/warehouse-transfer-requests/${requestToDelete}`);
      
      if (response.data.success) {
        setAlert({
          type: 'success',
          message: 'تم حذف الطلب بنجاح'
        });
        
        // Refresh the list
        handleRefresh();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error: any) {
      setAlert({
        type: 'error',
        message: error.response?.data?.message || error.message || 'حدث خطأ أثناء حذف الطلب'
      });
    } finally {
      setDeleteDialogOpen(false);
      setRequestToDelete(null);
    }
  };
  
  // Open approval confirmation dialog
  const confirmApproval = (requestId: string) => {
    setRequestToApprove(requestId);
    setApprovalDialogOpen(true);
  };
  
  // Approve a request
  const handleApproveRequest = async () => {
    if (!requestToApprove) return;
    
    try {
      const response = await axios.post(`${API_BASE_URL}/warehouse-transfer-requests/${requestToApprove}/approve`);
      
      if (response.data.success) {
        setAlert({
          type: 'success',
          message: 'تم الموافقة على طلب التحويل بنجاح'
        });
        
        // Refresh the list
        handleRefresh();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error: any) {
      setAlert({
        type: 'error',
        message: error.response?.data?.message || error.message || 'حدث خطأ أثناء الموافقة على الطلب'
      });
    } finally {
      setApprovalDialogOpen(false);
      setRequestToApprove(null);
    }
  };
  
  // تغيير حالة الطلب من تم الموافقة إلى تحت التنفيذ
  const confirmChangeStatus = (requestId: string) => {
    setRequestToChangeStatus(requestId);
    setChangeStatusDialogOpen(true);
  };
  
  // تنفيذ تغيير حالة الطلب من تم الموافقة إلى تحت التنفيذ
  const handleChangeStatusToPending = async () => {
    if (!requestToChangeStatus) return;
    
    try {
      const response = await axios.post(`${API_BASE_URL}/warehouse-transfer-requests/${requestToChangeStatus}/change-to-pending`);
      
      if (response.data.success) {
        setAlert({
          type: 'success',
          message: 'تم تغيير حالة الطلب إلى تحت التنفيذ بنجاح'
        });
        
        // تحديث القائمة
        handleRefresh();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error: any) {
      setAlert({
        type: 'error',
        message: error.response?.data?.message || error.message || 'حدث خطأ أثناء تغيير حالة الطلب'
      });
    } finally {
      setChangeStatusDialogOpen(false);
      setRequestToChangeStatus(null);
    }
  };
  
  // Open view dialog
  const handleViewRequest = async (requestId: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/warehouse-transfer-requests/${requestId}`);
      
      if (response.data.success) {
        setRequestToView(response.data.request);
        setViewDialogOpen(true);
      } else {
        throw new Error(response.data.message);
      }
    } catch (error: any) {
      setAlert({
        type: 'error',
        message: error.response?.data?.message || error.message || 'حدث خطأ أثناء جلب تفاصيل الطلب'
      });
    }
  };
  
  // Export requests to Excel
  const handleExportToExcel = async () => {
    try {
      // Prepare query parameters for export (same as current filters)
      const params = new URLSearchParams();
      
      // Add filters if they exist
      if (filters.status) params.append('status', filters.status);
      if (filters.fromWarehouse) params.append('fromWarehouse', filters.fromWarehouse);
      if (filters.toWarehouse) params.append('toWarehouse', filters.toWarehouse);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      
      // Notify the user that the export is starting
      setAlert({
        type: 'success',
        message: 'جاري تصدير البيانات...'
      });
      
      // Make the request to export the data
      const response = await axios.get(
        `${API_BASE_URL}/warehouse-transfer-requests/export?${params.toString()}`,
        { responseType: 'blob' }
      );
      
      // Create a download link and trigger the download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `warehouse-transfer-requests-${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Notify the user that the export is complete
      setAlert({
        type: 'success',
        message: 'تم تصدير البيانات بنجاح'
      });
    } catch (error: any) {
      console.error('Error exporting requests:', error);
      setAlert({
        type: 'error',
        message: 'حدث خطأ أثناء تصدير البيانات'
      });
    }
  };
  
  // Close alert
  const handleCloseAlert = () => {
    setAlert(null);
  };
  
  // Check if user can approve a specific request
  const canApproveRequest = (request: WarehouseTransferRequest): boolean => {
    if (!currentUser) return false;
    
    // Admin can approve any request
    if (currentUser.role === 'admin') return true;
    
    // Manager of the destination warehouse can approve
    if (currentUser.role === 'manager' && currentUser.warehouses && currentUser.warehouses.includes(request.toWarehouse._id)) {
      return true;
    }
    
    return false;
  };
  
  // Get status chip color based on status
  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };
  
  // Get status text based on status
  const getStatusText = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'تحت التنفيذ';
      case 'approved':
        return 'تم الموافقة';
      case 'rejected':
        return 'مرفوض';
      default:
        return status;
    }
  };
  
  // Format date for display
  const formatDate = (dateString: string): string => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: arDZ });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <Box sx={{ p: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2, ml: '-7%', width: '145%', pr: '12.5%' }}>
        <Typography variant="h5" sx={{ ml: '-5%' }}>طلبات التحويل بين المستودعات</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={isLoading}
          >
            تحديث
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'إخفاء الفلاتر' : 'عرض الفلاتر'}
          </Button>
          {hasPermission('warehouse-transfer-requests', 'list', 'view_page') && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ExportIcon />}
              onClick={handleExportToExcel}
              disabled={isLoading || requests.length === 0}
            >
              تصدير Excel
            </Button>
          )}
          {hasPermission('warehouse-transfer-requests', 'list', 'create') && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateRequest}
            >
              طلب تحويل جديد
            </Button>
          )}
        </Box>
      </Box>
      
      {/* Filters Section */}
      <Collapse in={showFilters}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>حالة الطلب</InputLabel>
                <Select
                  value={filters.status || ''}
                  label="حالة الطلب"
                  onChange={(e) => updateFilter('status', e.target.value || undefined)}
                >
                  <MenuItem value="">الكل</MenuItem>
                  <MenuItem value="pending">تحت التنفيذ</MenuItem>
                  <MenuItem value="approved">تم الموافقة</MenuItem>
                  <MenuItem value="rejected">مرفوض</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={warehouses}
                getOptionLabel={(option) => option.name}
                value={warehouses.find(w => w._id === filters.fromWarehouse) || null}
                onChange={(_, newValue) => updateFilter('fromWarehouse', newValue?._id || undefined)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="من المستودع"
                    size="small"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={warehouses}
                getOptionLabel={(option) => option.name}
                value={warehouses.find(w => w._id === filters.toWarehouse) || null}
                onChange={(_, newValue) => updateFilter('toWarehouse', newValue?._id || undefined)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="إلى المستودع"
                    size="small"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="من تاريخ"
                type="date"
                size="small"
                value={filters.startDate || ''}
                onChange={(e) => updateFilter('startDate', e.target.value || undefined)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="إلى تاريخ"
                type="date"
                size="small"
                value={filters.endDate || ''}
                onChange={(e) => updateFilter('endDate', e.target.value || undefined)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                onClick={resetFilters}
                fullWidth
              >
                إعادة تعيين الفلاتر
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>
      
      {/* Requests Table */}
      <Paper sx={{ width: '145%', overflow: 'hidden', mb: 1, ml: '-16%' }}>
        <TableContainer>
          <Table stickyHeader size="medium" aria-label="طلبات التحويل بين المستودعات" sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ width: '5%', fontWeight: 'bold', fontSize: '0.95rem' }}>حالة الأحداث</TableCell>
                <TableCell sx={{ width: '8%', fontWeight: 'bold', fontSize: '0.95rem' }}>رقم الطلب</TableCell>
                <TableCell sx={{ width: '13%', fontWeight: 'bold', fontSize: '0.95rem' }}>من المستودع</TableCell>
                <TableCell sx={{ width: '13%', fontWeight: 'bold', fontSize: '0.95rem' }}>إلى المستودع</TableCell>
                <TableCell sx={{ width: '9%', fontWeight: 'bold', fontSize: '0.95rem' }}>تاريخ الطلب</TableCell>
                <TableCell sx={{ width: '8%', fontWeight: 'bold', fontSize: '0.95rem' }}>عدد المنتجات</TableCell>
                <TableCell sx={{ width: '9%', fontWeight: 'bold', fontSize: '0.95rem' }}>الحالة</TableCell>
                <TableCell sx={{ width: '9%', fontWeight: 'bold', fontSize: '0.95rem' }}>تاريخ الإنشاء</TableCell>
                <TableCell sx={{ width: '9%', fontWeight: 'bold', fontSize: '0.95rem' }}>بواسطة</TableCell>
                <TableCell sx={{ width: '15%', fontWeight: 'bold', fontSize: '0.95rem' }}>إجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : requests.length > 0 ? (
                requests.map((request) => {
                  const referenceNumber = request._id;
                  const eventStatus = transferStatuses[referenceNumber];
                  
                  return (
                    <TableRow
                      key={request._id}
                      hover
                      sx={{
                        backgroundColor:
                          request.status === 'pending' ? 'rgba(255, 193, 7, 0.05)' :
                          request.status === 'approved' ? 'rgba(76, 175, 80, 0.05)' :
                          'inherit'
                      }}
                    >
                      <TableCell align="center">
                         <EventStatusIndicator
                           status={eventStatus}
                           loading={loadingStatuses}
                           referenceNumber={referenceNumber}
                           productCount={request.productVariants.length}
                         />
                       </TableCell>
                      <TableCell sx={{ fontSize: '0.9rem' }}>{request._id.substring(request._id.length - 6).toUpperCase()}</TableCell>
                      <TableCell sx={{ fontSize: '0.9rem' }}>{request.fromWarehouse?.name || 'غير محدد'}</TableCell>
                      <TableCell sx={{ fontSize: '0.9rem' }}>{request.toWarehouse?.name || 'غير محدد'}</TableCell>
                      <TableCell sx={{ fontSize: '0.9rem' }}>{formatDate(request.date)}</TableCell>
                      <TableCell sx={{ fontSize: '0.9rem', textAlign: 'center' }}>{request.productVariants.length}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(request.status)}
                          color={getStatusColor(request.status)}
                          size="medium"
                          sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.9rem' }}>{formatDate(request.createdAt)}</TableCell>
                      <TableCell sx={{ fontSize: '0.9rem' }}>{request.createdBy?.fullName || 'غير محدد'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Tooltip title="عرض التفاصيل">
                            <IconButton
                              color="info"
                              size="medium"
                              onClick={() => handleViewRequest(request._id)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          
                          {request.status === 'pending' && (
                            <>
                              {hasPermission('warehouse-transfer-requests', 'list', 'edit') && (
                                <Tooltip title="تعديل">
                                  <IconButton
                                    color="primary"
                                    size="medium"
                                    onClick={() => handleEditRequest(request._id)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              
                              {hasPermission('warehouse-transfer-requests', 'list', 'delete') && (
                                <Tooltip title="حذف">
                                  <IconButton
                                    color="error"
                                    size="medium"
                                    onClick={() => confirmDelete(request._id)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              
                              {canApproveRequest(request) && hasPermission('warehouse-transfer-requests', 'list', 'approve') && (
                                <Tooltip title="موافقة">
                                  <IconButton
                                    color="success"
                                    size="medium"
                                    onClick={() => confirmApproval(request._id)}
                                  >
                                    <ApproveIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                          
                          {request.status === 'approved' && hasPermission('warehouse-transfer-requests', 'list', 'change_to_pending') && (
                            <>
                              <Tooltip title="تغيير إلى تحت التنفيذ">
                                <IconButton
                                  color="warning"
                                  size="medium"
                                  onClick={() => confirmChangeStatus(request._id)}
                                >
                                  <RejectIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    لا توجد طلبات تحويل
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalRequests}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="عدد الصفوف:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
        />
      </Paper>
      
      {/* View Request Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        fullWidth
        maxWidth="xl"
      >
        {requestToView && (
          <>
            <DialogTitle>
              تفاصيل طلب التحويل
              <Chip
                label={getStatusText(requestToView.status)}
                color={getStatusColor(requestToView.status)}
                size="small"
                sx={{ ml: 2 }}
              />
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>معلومات الطلب</Typography>
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 120 }}>رقم الطلب:</Typography>
                    <Typography variant="body2">{requestToView._id}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 120 }}>من المستودع:</Typography>
                    <Typography variant="body2">{requestToView.fromWarehouse?.name || 'غير محدد'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 120 }}>إلى المستودع:</Typography>
                    <Typography variant="body2">{requestToView.toWarehouse?.name || 'غير محدد'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 120 }}>تاريخ الطلب:</Typography>
                    <Typography variant="body2">{formatDate(requestToView.date)}</Typography>
                  </Box>
                  {requestToView.notes && (
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 120 }}>ملاحظات:</Typography>
                      <Typography variant="body2">{requestToView.notes}</Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>معلومات المستخدم</Typography>
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 120 }}>تم الإنشاء بواسطة:</Typography>
                    <Typography variant="body2">{requestToView.createdBy?.fullName || 'غير محدد'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 120 }}>تاريخ الإنشاء:</Typography>
                    <Typography variant="body2">{formatDate(requestToView.createdAt)}</Typography>
                  </Box>
                  {requestToView.approvedBy && (
                    <>
                      <Box sx={{ display: 'flex', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 120 }}>تمت الموافقة بواسطة:</Typography>
                        <Typography variant="body2">{requestToView.approvedBy?.fullName || 'غير محدد'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 120 }}>تاريخ الموافقة:</Typography>
                        <Typography variant="body2">{formatDate(requestToView.approvedAt || '')}</Typography>
                      </Box>
                    </>
                  )}
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>المنتجات</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="medium">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 200, fontWeight: 'bold' }}>المنتج</TableCell>
                      <TableCell sx={{ minWidth: 250, fontWeight: 'bold' }}>المتغيرات</TableCell>
                      <TableCell sx={{ minWidth: 100, fontWeight: 'bold' }}>الكمية</TableCell>
                      <TableCell sx={{ minWidth: 100, fontWeight: 'bold' }}>الوحدة</TableCell>
                      <TableCell sx={{ minWidth: 200, fontWeight: 'bold' }}>SKU / الباركود</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requestToView.productVariants.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.product?.name || 'غير محدد'}</TableCell>
                        <TableCell>
                          {item.variantId ? (
                            <Box sx={{ maxWidth: 200 }}>
                              {Object.entries(item.variantAttributes).map(([key, value]) => (
                                <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 1 }}>
                                    {key}:
                                  </Typography>
                                  <Typography variant="caption">
                                    {value}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            'المنتج الأساسي'
                          )}
                        </TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{item.unit?.name || 'غير محدد'}</TableCell>
                        <TableCell>
                          {item.sku && (
                            <Typography variant="caption" display="block">
                              <strong>SKU:</strong> {item.sku}
                            </Typography>
                          )}
                          {item.barcode && (
                            <Typography variant="caption" display="block">
                              <strong>باركود:</strong> {item.barcode}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {requestToView.status === 'pending' && canApproveRequest(requestToView) && hasPermission('warehouse-transfer-requests', 'list', 'approve') && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<ApproveIcon />}
                    onClick={() => {
                      setViewDialogOpen(false);
                      confirmApproval(requestToView._id);
                    }}
                  >
                    الموافقة على الطلب
                  </Button>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewDialogOpen(false)}>إغلاق</Button>
              {requestToView.status === 'pending' && hasPermission('warehouse-transfer-requests', 'list', 'edit') && (
                <Button
                  color="primary"
                  onClick={() => {
                    setViewDialogOpen(false);
                    handleEditRequest(requestToView._id);
                  }}
                >
                  تعديل
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من حذف طلب التحويل هذا؟
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>إلغاء</Button>
          <Button color="error" onClick={handleDeleteRequest} autoFocus>
            حذف
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Approval Confirmation Dialog */}
      <Dialog
        open={approvalDialogOpen}
        onClose={() => setApprovalDialogOpen(false)}
      >
        <DialogTitle>تأكيد الموافقة</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من الموافقة على طلب التحويل هذا؟ سيتم تحديث كميات المنتجات في كلا المستودعين.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialogOpen(false)}>إلغاء</Button>
          <Button color="success" onClick={handleApproveRequest} autoFocus>
            موافقة
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* تغيير الحالة إلى تحت التنفيذ */}
      <Dialog
        open={changeStatusDialogOpen}
        onClose={() => setChangeStatusDialogOpen(false)}
      >
        <DialogTitle>تأكيد تغيير الحالة</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من تغيير حالة طلب التحويل هذا من "تم الموافقة" إلى "تحت التنفيذ"؟
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeStatusDialogOpen(false)}>إلغاء</Button>
          <Button color="warning" onClick={handleChangeStatusToPending} autoFocus>
            تغيير الحالة
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Alert Snackbar */}
      <Snackbar
        open={!!alert}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={alert?.type || 'info'} sx={{ width: '100%' }}>
          {alert?.message || ''}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WarehouseTransferRequestList;